import { Keyboard, Text, View } from 'react-native'
import { Link } from 'expo-router'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { COUNTRY_CODES } from '@/constants/config'
import { getAuthErrorCode, getAuthErrorContext, getAuthErrorStatus } from '@/stores/auth'
import { Button, FormScrollView, useToast } from '@/components/base'
import { FormInput, PhoneInput } from '@/components/form-control'
import { OtpForm } from '@/components/form/OtpForm'
import { Verified } from '@/components/icon'
import { useLoadingIndicator } from '@/components/LoadingIndicator'

export type RegisterValues = {
  phoneNumber: {
    code: string
    number: string
  }
  newPassword: string
  newPasswordConfirm: string
  verifyCode: string
}

const defaultValues: RegisterValues = {
  phoneNumber: {
    code: COUNTRY_CODES.China.dial_code,
    number: ''
  },
  newPassword: '',
  newPasswordConfirm: '',
  verifyCode: ''
}

interface RegisterFormProps {
  step: 'register' | 'verify' | 'finish'
  onRegister: SubmitHandler<RegisterValues>
  onSubmit: SubmitHandler<RegisterValues>
  onFinish: () => void
  requestOTP: SubmitHandler<RegisterValues>
}

// TODO, prototype, need to implement
export function RegisterForm({ step, onRegister, onSubmit, onFinish, requestOTP }: RegisterFormProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const loading = useLoadingIndicator()
  const { control, handleSubmit, setFocus, watch, setError } = useForm<RegisterValues>({ defaultValues })
  const phoneNumber = watch('phoneNumber.number')
  const newPassword = watch('newPassword')
  const newPasswordConfirm = watch('newPasswordConfirm')
  const verifyCode = watch('verifyCode')

  async function preOnRegister(data: RegisterValues) {
    Keyboard.dismiss()
    if (phoneNumber.length > 15 || !/^\d+$/.test(phoneNumber)) {
      setError('phoneNumber.number', { message: t('auth:hint.phoneNumberInvalid') })
      return
    }
    if (newPassword.length < 8 || !/\d/.test(newPassword) || !/[a-zA-Z]/.test(newPassword)) {
      setError('newPassword', { message: t('auth:hint.password') })
      return
    }
    if (newPassword !== newPasswordConfirm) {
      setError('newPasswordConfirm', { message: t('auth:hint.mismatch') })
      return
    }
    const strippedZeroPhoneNumber = Number(data.phoneNumber.number).toString()
    try {
      await onRegister({ ...data, phoneNumber: { ...data.phoneNumber, number: strippedZeroPhoneNumber } })
    } catch (e: any) {
      const errorCode = getAuthErrorCode(e)
      const status = getAuthErrorStatus(e)
      const context = getAuthErrorContext(e)

      if (errorCode === 1023) {
        toast.show({ variant: 'error', description: t('auth:toast.error.sendVerificationCodeTooFast') })
      } else if (errorCode === 1025 || errorCode === 1024 || status === 429) {
        toast.show({ variant: 'error', description: t('auth:toast.error.sendVerificationCodeQuotaReached') })
      } else if (errorCode === 1022) {
        toast.show({ variant: 'error', description: t('auth:toast.error.smsServiceUnavailable') })
      } else if (errorCode === 1043) {
        setError('phoneNumber.number', { message: t('auth:hint.repeatedRegister') })
      } else if (errorCode === 1042) {
        if (context === 'User domain error: phone is invalid') {
          setError('phoneNumber.number', { message: t('auth:hint.phoneNumberInvalid') })
        } else if (context === 'User domain error: password is invalid') {
          setError('newPassword', { message: t('auth:hint.passwordInvalid') })
        }
      } else if (errorCode === 1007) {
        toast.show({ variant: 'error', description: t('auth:toast.error.noAccess') })
      } else if (status === undefined) {
        toast.show({ variant: 'error', description: t('auth:toast.error.networkError') })
      } else {
        toast.show({ variant: 'error', description: t('auth:toast.error.sendVerificationCode') })
      }
    }
  }

  switch (step) {
    case 'register':
      return (
        <FormScrollView className={'pb-4'}>
          <View className={'gap-y-4 px-4'}>
            <View className={'justify-center pb-3 pt-2'}>
              <Text className={'text-2xl font-semibold text-text-primary'}>{t('auth:register')}</Text>
            </View>
            <View className={'gap-y-2'}>
              <PhoneInput
                control={control}
                name={{ code: 'phoneNumber.code', number: 'phoneNumber.number' }}
                label={t('auth:label:phoneNumber')}
                placeholder={t('auth:placeholder.phoneNumber')}
                textContentType={'none'}
                autoCorrect={false}
                returnKeyType={'next'}
                onSubmitEditing={() => setFocus('newPassword')}
              />
              <FormInput
                control={control}
                name={'newPassword'}
                label={t('auth:label.loginPassword')}
                hint={t('auth:hint.password')}
                type={'password'}
                placeholder={t('auth:placeholder.loginPassword')}
                textContentType={'newPassword'}
                autoCapitalize={'none'}
                autoCorrect={false}
                returnKeyType={'next'}
                onSubmitEditing={() => setFocus('newPasswordConfirm')}
                maxLength={64}
              />
              <FormInput
                control={control}
                name={'newPasswordConfirm'}
                label={t('auth:label.confirmLoginPassword')}
                type={'password'}
                placeholder={t('auth:placeholder.confirmLoginPassword')}
                textContentType={'newPassword'}
                autoCapitalize={'none'}
                autoCorrect={false}
                returnKeyType={'go'}
                onSubmitEditing={handleSubmit(preOnRegister)}
                maxLength={64}
              />
            </View>
            <View className={'gap-y-10'}>
              <Button
                onPress={handleSubmit(preOnRegister)}
                disabled={!phoneNumber || !newPassword || !newPasswordConfirm || loading.isLoading}
              >
                {t('auth:register')}
              </Button>
              <Text className={'text-sm text-text-secondary'}>
                <Trans
                  i18nKey={'auth:haveAccount'}
                  components={[<Link key={0} className={'font-medium text-info'} href={'/login'} />]}
                />
              </Text>
            </View>
          </View>
        </FormScrollView>
      )
    case 'verify':
      return (
        <View className={'flex-1 bg-background-secondary'}>
          <OtpForm
            control={control}
            onSubmit={handleSubmit(onSubmit)}
            requestOTP={handleSubmit(requestOTP)}
            automaticallyStartTimer={true}
            verifyCode={verifyCode}
          />
        </View>
      )
    case 'finish':
      return (
        <View className={'flex-1 gap-y-4 p-4'}>
          <View className={'flex-1 items-center justify-center gap-y-3'}>
            <Verified className={'size-20 text-success'} />
            <Text className={'text-2xl font-semibold text-text-primary'}>{t('auth:registerSuccess')}</Text>
          </View>
          <Button onPress={onFinish}>{t('auth:complete')}</Button>
        </View>
      )
    default:
      return null
  }
}
